/**
 * <header-popover> — shared non-modal popover for header dropdowns
 * (search, cart, localization). Light-DOM web component with mutual
 * exclusion: opening one instance auto-closes any other.
 *
 * DOM contract:
 *   <header-popover>
 *     <button data-trigger aria-haspopup="dialog">…</button>
 *     <div data-panel role="dialog" aria-label="…" hidden>…</div>
 *   </header-popover>
 *
 * State is expressed via `data-open` attributes:
 *   - Host element: `<header-popover data-open>` when open
 *   - Trigger button: `<button data-open>` when open (for active styling)
 *   - Shared backdrop: `<div data-popover-backdrop data-open>` when any popover is open
 */

class HeaderPopover extends HTMLElement {
  /** @type {HeaderPopover | null} The currently open instance */
  static #current = null;

  /** @type {AbortController} Connected-lifecycle listeners */
  #controller = new AbortController();

  /** @type {AbortController | null} Listeners active only while open */
  #openController = null;

  /** @type {HTMLElement | null} */
  get #trigger() {
    return this.querySelector('[data-trigger]');
  }

  /** @type {HTMLElement | null} */
  get #panel() {
    return this.querySelector('[data-panel]');
  }

  /** Whether the popover is currently open */
  get open() {
    const panel = this.#panel;
    return panel ? !panel.hidden : false;
  }

  /** @returns {HTMLElement | null} */
  static get #backdrop() {
    return document.querySelector('[data-popover-backdrop]');
  }

  static #updateBackdrop() {
    const backdrop = HeaderPopover.#backdrop;
    if (!backdrop) return;

    if (HeaderPopover.#current) {
      backdrop.dataset.open = '';
    } else {
      delete backdrop.dataset.open;
    }
  }

  connectedCallback() {
    const trigger = this.#trigger;
    if (!trigger) return;

    const { signal } = this.#controller;

    trigger.addEventListener('click', () => this.toggle(), { signal });

    for (const btn of this.querySelectorAll('[data-close-popover]')) {
      btn.addEventListener('click', () => this.hide(), { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#openController?.abort();

    if (HeaderPopover.#current === this) {
      HeaderPopover.#current = null;
      HeaderPopover.#updateBackdrop();
    }
  }

  // ── Public API ──────────────────────────────────────────────────

  show() {
    const panel = this.#panel;
    if (!panel || this.open) return;

    // Mutual exclusion — close any other open instance
    if (HeaderPopover.#current && HeaderPopover.#current !== this) {
      HeaderPopover.#current.hide({ returnFocus: false });
    }
    HeaderPopover.#current = this;

    panel.hidden = false;
    this.dataset.open = '';
    if (this.#trigger) this.#trigger.dataset.open = '';
    HeaderPopover.#updateBackdrop();

    // Bind open-scoped listeners
    this.#openController = new AbortController();
    const { signal } = this.#openController;

    document.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    // Defer click-outside so the triggering click doesn't immediately close
    requestAnimationFrame(() => {
      document.addEventListener('click', this.#onDocumentClick, { signal });
    });

    // Focus the [autofocus] element if one exists
    const target = panel.querySelector('[autofocus]');
    if (target instanceof HTMLElement) target.focus();

    this.dispatchEvent(new CustomEvent('popover:open', { bubbles: true }));
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.returnFocus] — restore focus to trigger (default true)
   */
  hide({ returnFocus = true } = {}) {
    const panel = this.#panel;
    if (!panel || !this.open) return;

    this.#openController?.abort();
    this.#openController = null;

    if (HeaderPopover.#current === this) {
      HeaderPopover.#current = null;
    }

    delete this.dataset.open;
    if (this.#trigger) delete this.#trigger.dataset.open;
    HeaderPopover.#updateBackdrop();

    panel.hidden = true;

    this.dispatchEvent(new CustomEvent('popover:close', { bubbles: true }));

    if (returnFocus) {
      this.#trigger?.focus();
    }
  }

  toggle() {
    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  // ── Private handlers ────────────────────────────────────────────

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.hide();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    // Stay open when focus moves within the component, or to null/body
    // (null/body means the user clicked a non-focusable area)
    if (next instanceof Node && this.contains(next)) return;
    if (!next || next === document.body) return;
    this.hide({ returnFocus: false });
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    // Click inside the component — do nothing
    if (this.contains(target)) return;
    this.hide({ returnFocus: false });
  };
}

if (!customElements.get('header-popover')) {
  customElements.define('header-popover', HeaderPopover);
}
